/**
 * @file add-edit-target-modal.tsx
 * @module app/modals/add-edit-target-modal
 * @description Renders the add edit target modal modal dialog for confirming actions, capturing workflow input, and reporting outcomes to its parent screen.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useState } from "react";
import { Button, Form, FormLabel, Modal } from "react-bootstrap";
import { CustomSelectWhite } from "../custom/Select/CustomSelectWhite";
import CrossSvg from "../../_admin/assets/media/svg_uTrade/cross-rounded-gray.svg";
import clsx from "clsx";
import APICallService from "../../api/apiCallService";
import { targetManagement } from "../../api/apiEndPoints";
import { targetManagementJson } from "../../api/apiJSON/targetManagement";
import { useWarehouse } from "../context/WarehouseContext";
import { TargetManagement, Add, Edit } from "../../utils/constants";
import { success } from "../../Global/toast";
import { masterToast } from "../../utils/toast";
import { REPRESENTATIVE_TARGET_PERIOD } from "../../utils/staticJSON";
import Method from "../../utils/methods";

interface AddEditTargetModalProps {
  show: boolean;
  onHide: () => void;
  targetData?: any;
  onSave: () => void;
}

const AddEditTargetModal: React.FC<AddEditTargetModalProps> = ({
  show,
  onHide,
  targetData,
  onSave,
}) => {
  const [salesRep, setSalesRep] = useState<any>(null);
  const [targetAmount, setTargetAmount] = useState<string>("");
  const [period, setPeriod] = useState<any>(null);
  const [notes, setNotes] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const { selectedWarehouse } = useWarehouse();

  const [validation, setValidation] = useState({
    salesRep: false,
    targetAmount: false,
    period: false,
  });


  const [salesRepOptions, setSalesRepOptions] = useState<any[]>([]);

  useEffect(() => {
    if (show) {
      // fetchSalesReps();
    }
  }, [show]);

  const fetchSalesReps = async () => {
    // This is a placeholder for fetching actual sales reps
    const apiService = new APICallService(
      targetManagement.list, // Or whichever endpoint returns reps
      { role: targetData?.role[0]?.type, needCount: false },
      "",
      "",
      "",
      "",
      TargetManagement,
      selectedWarehouse?.value || null
    );

    const response = await apiService.callAPI();
    if (response?.records) {
      setSalesRepOptions(response.records.map((item: any) => ({
        label: item.name,
        value: item.email,
        title: item.name,
        _id: item._id
      })));
    }
  };

  useEffect(() => {
    if (show) {
      if (targetData) {
        // Auto-fill sales rep from targetData if available
        const repFromData = {
          label: targetData.repName || targetData.name,
          value: targetData.email,
          title: targetData.repName || targetData.name,
          _id: targetData._id || targetData.id
        };

        // Try to find the rep in fetched options for consistency, 
        // but fallback to targetData's info to ensure it's auto-filled immediately.
        const foundRep = salesRepOptions.find(
          (opt) => opt.value === targetData.email || opt.label === (targetData.repName || targetData.name)
        );
        
        setSalesRep(foundRep || repFromData);

        const amount = targetData?.currentSalesTarget?.target || "";
        setTargetAmount(amount ? amount.toString() : "");

        setPeriod(
          REPRESENTATIVE_TARGET_PERIOD.find(
            (opt) => opt.value == targetData?.currentSalesTarget?.type
          ) || null
        );
        setNotes(targetData?.currentSalesTarget?.notes || "");
      } else {
        setSalesRep(null);
        setTargetAmount("");
        setPeriod(null);
        setNotes("");
      }
      setValidation({
        salesRep: false,
        targetAmount: false,
        period: false,
      });
    }
  }, [targetData, show, salesRepOptions]);

  const handleSave = async () => {
    const tempValidation = {
      salesRep: !salesRep,
      targetAmount: !targetAmount || targetAmount === "0",
      period: !period,
    };

    setValidation(tempValidation);

    const isValid = Object.values(tempValidation).every((v) => !v);

    if (isValid) {
      setLoading(true);

      const now = new Date();
      let startDate = "";
      let endDate = "";

      if (period?.value === 1) {
        // Monthly: today to today + 1 month - 1 day
        startDate = Method.convertDateToFormat(now, 'MM-DD-YYYY');
        endDate = Method.convertDateToFormat(new Date(now.getFullYear(), now.getMonth() + 1, now.getDate() - 1), 'MM-DD-YYYY');
      } else if (period?.value === 2) {
        // Quarterly: today to today + 3 months - 1 day
        startDate = Method.convertDateToFormat(now, 'MM-DD-YYYY');
        endDate = Method.convertDateToFormat(new Date(now.getFullYear(), now.getMonth() + 3, now.getDate() - 1), 'MM-DD-YYYY');
      } else if (period?.value === 3) {
        // Yearly: today to today + 1 year - 1 day
        startDate = Method.convertDateToFormat(now, 'MM-DD-YYYY');
        endDate = Method.convertDateToFormat(new Date(now.getFullYear() + 1, now.getMonth(), now.getDate() - 1), 'MM-DD-YYYY');
      }

      const apiService = new APICallService(
        targetManagement.addEditTarget,
        targetManagementJson.addEditTarget({
          // salesRepId: salesRep?._id || salesRep?.value,
          target: targetAmount,
          type: period?.value,
          notes: notes,
          startDate: startDate,
          endDate: endDate,
        }),
        { _id: salesRep?._id || "" },
        "",
        "",
        "",
        TargetManagement,
        selectedWarehouse?.value || null
      );

      const response = await apiService.callAPI();
      if (response) {
        success(targetData?.currentSalesTarget?.reference ? masterToast.editTarget : masterToast.assignTarget);
        onSave();
        onHide();
      }
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="modal-md"
      contentClassName="rounded-15px border-0 shadow-sm"
    >
      <Modal.Header className="border-0 pb-0 position-relative d-flex justify-content-center pt-8">
        <img
          className="close-inner-top position-absolute start-0 ms-5"
          width={40}
          height={40}
          src={CrossSvg}
          alt="closebutton"
          onClick={onHide}
          style={{ top: "25px", cursor: "pointer" }}
        />
        <Modal.Title className="fw-bolder fs-20 text-dark">
          {targetData && targetData?.currentSalesTarget?.reference
            ? "Edit Targets"
            : "Assign Targets"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-10 pb-10 pt-8">
        <Form>
          {/* <div className="mb-7">
            <FormLabel className="fs-16 fw-500 mb-3 text-dark">
              Select a {targetData?.role[0]?.type == 1 ? "Sales Manager" :"Sales Rep"}
            </FormLabel>
            <CustomSelectWhite
              options={salesRepOptions}
              value={salesRep}
              onChange={(val: any) => {
                setSalesRep(val);
                setValidation({ ...validation, salesRep: false });
              }}
              placeholder="Select Sales Rep"
              isDisabled={targetData ? true : false}
              border={validation.salesRep ? "#e55451" : ""}
            />
          </div> */}

          <div className="mb-7">
            <FormLabel className="fs-16 fw-500 mb-3 text-dark">
              Enter Target Amount
            </FormLabel>
            <div className="position-relative">
              <Form.Control
                type="number"
                min={0}
                step="1"
                className={clsx(
                  "form-control-custom bg-white",
                  validation.targetAmount ? "border-danger" : ""
                )}
                placeholder="Type here"
                value={targetAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || (Number(value) > 0 && !value.startsWith("-"))) {
                    setTargetAmount(value);
                    setValidation({ ...validation, targetAmount: false });
                  }
                }}
              />
              <span className="position-absolute end-0 top-50 translate-middle-y me-5 text-gray-500 fs-14 fw-500">
                Tsh
              </span>
            </div>
          </div>

          <div className="mb-7">
            <FormLabel className="fs-16 fw-500 mb-3 text-dark">
              Select Target Period
            </FormLabel>
            <CustomSelectWhite
              options={REPRESENTATIVE_TARGET_PERIOD.filter(opt => opt.value !== -1)}
              value={period}
              onChange={(val: any) => {
                setPeriod(val);
                setValidation({ ...validation, period: false });
              }}
              placeholder="Select Period"
              border={validation.period ? "#e55451" : ""}
            />
          </div>

          <div className="mb-10">
            <FormLabel className="fs-16 fw-500 mb-3 text-dark">
              Notes (optional)
            </FormLabel>
            <Form.Control
              as="textarea"
              className="form-control-custom bg-white"
              rows={4}
              placeholder="Type here"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <Button
            variant="primary"
            className="uw-btn uw-btn-primary uw-btn-lg w-100 btn-lg shadow-sm"
            onClick={handleSave}
            disabled={loading}
          >
            {!loading ? (
              <span className="fs-16 fw-bold">
                Save Target for{" "}
                {salesRep ? salesRep.label.split(" ")[0] : "..."}
              </span>
            ) : (
              <div className="d-flex justify-content-center">
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </div>
            )}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditTargetModal;
